import MembershipProfile from '@components/membership/membership-profile/MembershipProfile'
import { Subscription as SubscriptionModel } from '@models/membership/Subscription'
import {
  detachCouponFromSubscription,
  reactivateSubscription,
} from '@services/membership/SubscriptionService'
import { useAppSelector } from '@store/hooks'
import { selectSubscriptionById } from '@store/membership/SubscriptionReducer'
import { EllipsisMiddle } from '@supplement-bacon/react-toolkit/components'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { SBRM, SBRMAction } from '@supplement-bacon/sbrm-react'
import { currencyFormat } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import Feature from '@components/Feature'
import { capitalizeFirstLetter } from '@utils/string'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Popconfirm,
  Space,
  Tag,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { FC } from 'react'
import Coupon from '../coupon/Coupon'
import AttachCoupon from './AttachCoupon'
import ChangePlan from './ChangePlan'
import { TagManager } from './TagManager'

const { Text } = Typography

type Props = SBModelComponent<SubscriptionModel> & {
  onChange?: () => void | Promise<unknown>
}

const SubscriptionDescription: FC<Props> = ({ id, model, onChange }) => {
  const item = model ?? useAppSelector(selectSubscriptionById(id!))

  if (!item) {
    return <></>
  }

  return (
    <Card styles={{ body: { padding: 16 } }}>
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="UUID">
          <Text copyable code>
            {item.uuid}
          </Text>
        </Descriptions.Item>

        <Descriptions.Item label="Tags">
          <TagManager id={item.id} onChange={onChange} />
        </Descriptions.Item>

        <Descriptions.Item label="Status">
          <Tag
            color={
              item.status === 'active'
                ? 'green'
                : item.status === 'awaiting_transfer' ||
                    item.status === 'awaiting_transfer_renewal'
                  ? 'gold'
                  : 'default'
            }
          >
            {item.status}
          </Tag>
        </Descriptions.Item>

        {item.plan?.program && (
          <Descriptions.Item label="Program">
            {item.plan.program.en.name}
          </Descriptions.Item>
        )}
        {item.plan && (
          <Descriptions.Item label="Plan">
            <Space wrap>
              {item.plan.en.name}
              {(item.status === 'active' || item.status === 'to_cancel') && (
                // Inline: a button cannot be framed, so a preview gets its tag.
                <Feature flag="changeSubscriptionPlan" preview="inline">
                  <ChangePlan subscription={item} onChange={onChange} />
                </Feature>
              )}
            </Space>
          </Descriptions.Item>
        )}

        {item.plan?.price && (
          <Descriptions.Item label="Price">
            <Text>{currencyFormat(item.plan.price, true)}</Text>
          </Descriptions.Item>
        )}

        {item.plan?.price && (
          <Descriptions.Item label="Joining Fee">
            <Text>{currencyFormat(item.plan.membershipFees, true)}</Text>
          </Descriptions.Item>
        )}

        <Descriptions.Item label="Coupon">
          <Space wrap>
            {item.coupon ? (
              <Coupon.Avatar showDetails id={item.coupon} />
            ) : (
              <Text type="secondary">No coupon</Text>
            )}
            <AttachCoupon subscription={item} onChange={onChange} />
            {item.coupon && (
              <Popconfirm
                title="Detach coupon?"
                description="The subscription will fall back to the normal pay flow on its next renewal."
                okText="Detach"
                cancelText="Cancel"
                onConfirm={async () => {
                  await detachCouponFromSubscription(item.id)
                  await onChange?.()
                }}
              >
                <Button size="small" danger>
                  Detach
                </Button>
              </Popconfirm>
            )}
          </Space>
        </Descriptions.Item>

        <Descriptions.Item label="Next renewal">
          {item.renewalDate ? (
            <>
              {dayjs(item.renewalDate).format('DD/MM/YYYY')} -{' '}
              {capitalizeFirstLetter(dayjs(item.renewalDate).fromNow())}
            </>
          ) : (
            '--'
          )}
        </Descriptions.Item>

        <Descriptions.Item label="Last update">
          {dayjs(item.updatedAt).format('DD/MM/YYYY')} -{' '}
          {capitalizeFirstLetter(dayjs(item.updatedAt).fromNow())}
        </Descriptions.Item>

        <Descriptions.Item label="Submission date">
          {dayjs(item.createdAt).format('DD/MM/YYYY')} -{' '}
          {capitalizeFirstLetter(dayjs(item.createdAt).fromNow())}
        </Descriptions.Item>

        {item.membershipProfiles && item.membershipProfiles.length > 0 && (
          <Descriptions.Item label="Associated members">
            <Space wrap>
              {item.membershipProfiles.map((profile) => (
                <MembershipProfile.Avatar id={profile} key={profile} />
              ))}
            </Space>
          </Descriptions.Item>
        )}

        <Descriptions.Item label="PSP order reference">
          {item.id}
        </Descriptions.Item>

        <Descriptions.Item label="Subscription portal">
          <EllipsisMiddle
            suffixCount={20}
            copyable={{ text: item.frontendUrl }}
          >
            {item.frontendUrl}
          </EllipsisMiddle>
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 10 }}>
        {item.status === 'active' && (
          <SBRM.Actions
            actions={[SBRMAction.Update]}
            entity={Entity.membershipSubscriptions}
            entityId={item.id}
            sections={['cancellation']}
            trigger={
              <Button type="primary" danger block>
                Cancel subscription
              </Button>
            }
            actionLayoutsOverride={{
              [SBRMAction.Update]: {
                modal: { content: 'form' },
                buttons: {
                  actionButtonPropsOverride: {
                    danger: true,
                    children: 'Cancel subscription',
                  },
                },
              },
            }}
          />
        )}

        {item.status === 'to_cancel' && (
          <Alert
            title={`The cancellation is scheduled for ${dayjs(item.renewalDate).format('MMMM DD, YYYY')}`}
            type="error"
            showIcon
            action={
              <Popconfirm
                title="Revert cancellation ?"
                description="The subscription will renew at the end of the current period."
                okText="Yes"
                cancelText="No"
                onConfirm={async () => {
                  await reactivateSubscription(item.id)
                  window.location.reload()
                }}
              >
                <Button variant="outlined" danger size="small">
                  Revert cancellation
                </Button>
              </Popconfirm>
            }
          />
        )}

        {item.status === 'cancelled' && (
          <Alert
            title="The subscription has been canceled."
            type="error"
            showIcon
          />
        )}
      </div>
    </Card>
  )
}

type SubscriptionDescriptionType = {
  Description: typeof SubscriptionDescription
}

export { SubscriptionDescription, type SubscriptionDescriptionType }
